import {
  Box,
  Modal,
  Button,
  Divider,
  Typography,
  FormControl,
  OutlinedInput,
  FormHelperText,
  InputLabel,
} from '@mui/material';
import { t } from 'i18next';
import { OTPInput } from '@components';
import { HttpErrorMessage } from '@views/pages/Auth/common';
import { postRequest } from '@config/axios/axios.config';
import { paths } from '@common/types';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import _ from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { formatApiError } from '@common/errors/error-formatting.ts';

interface ISetupSmsMFAModalProps {
  onClose: () => void;
  isOpen: boolean;
}

export const SetupSmsMFAModal = ({
  onClose,
  isOpen,
}: ISetupSmsMFAModalProps) => {
  const [otpValue, setOTPValue] = useState<string>('');
  const [hasError, setHasError] = useState<boolean>(false);
  const [mfaPhoneNumber, setMFAPhoneNumber] = useState<string>('');
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [showSendCode, setShowSendCode] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const handleOnChangeOtpValue = (newValue: string) => {
    setOTPValue(newValue);
  };

  const handleChangePhoneNumber = (newNumber: string) => {
    setMFAPhoneNumber(newNumber);
    const isValid = isValidPhoneNumber(newNumber, 'US');
    if (!isValid) {
      setError('Must match a valid phone number format');
    } else {
      setError(undefined);
    }
  };

  const registerMFARequest = async () => {
    setLoading(true);
    try {
      await postRequest<
        paths['/profile/new-mfa-method']['post']['requestBody']['content']['application/json'],
        paths['/profile/new-mfa-method']['post']['responses']['201']['content']['application/json']
      >('/profile/new-mfa-method', {
        type: 'sms',
        mobile_phone_number: mfaPhoneNumber,
      });

      toast.success('Code sent successfully');
      setShowSendCode(true);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = () => {
    registerMFARequest();
  };

  useEffect(() => {
    async function fetchMyAPI() {
      try {
        setHasError(false);

        await postRequest<
          paths['/profile/new-mfa-method/confirm']['post']['requestBody']['content']['application/json'],
          paths['/profile/new-mfa-method/confirm']['post']['responses']['200']['content']['application/json']
        >('/profile/new-mfa-method/confirm', {
          type: 'sms',
          secret: otpValue,
        });

        queryClient.invalidateQueries({ queryKey: ['profile'] });

        toast.success('MFA setup successful');
        onClose();
      } catch {
        setHasError(true);
      }
    }

    if (otpValue.length === 6 && !otpValue.includes(' ')) {
      fetchMyAPI();
    }
  }, [otpValue]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          width: '50%',
          maxWidth: '450px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '1.5rem',
            gap: '16px',
            width: '100%',
            borderRadius: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              padding: 0,
            }}
          >
            <VerifiedUserIcon sx={{ fontSize: '3rem' }} color='primary' />
            <Typography
              sx={{ fontWeight: '600', fontSize: '1.5rem', color: 'primary' }}
            >
              Verify your account
            </Typography>
          </Box>

          <FormControl sx={{ width: '100%' }} variant='outlined'>
            <InputLabel required size='medium' id={`phone-number-label`}>
              Phone number
            </InputLabel>
            <OutlinedInput
              size='medium'
              id='phone-number'
              name='phone-number'
              type={'text'}
              label={'Phone number'}
              error={!_.isNil(error)}
              onChange={e => handleChangePhoneNumber(e.target.value)}
              value={mfaPhoneNumber}
              fullWidth
              required
            />
            {!!error && (
              <FormHelperText error id='phone-number-error'>
                {error}
              </FormHelperText>
            )}
          </FormControl>

          <Box
            sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button
              variant='contained'
              color='secondary'
              loadingPosition='center'
              loading={loading}
              disabled={loading || !!error}
              sx={{ color: 'white' }}
              onClick={handleSendCode}
            >
              SEND
            </Button>
          </Box>

          {showSendCode && (
            <>
              <Divider sx={{ margin: '0.3rem', width: '100%' }} />

              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 'regular',
                  textAlign: 'center',
                }}
              >
                Enter your 6-digit Bridge verification code sent to{' '}
                {mfaPhoneNumber}.
              </Typography>

              {hasError && (
                <HttpErrorMessage
                  message={
                    'The MFA code you entered is invalid. Please check your authenticator app or SMS and try again.'
                  }
                />
              )}

              <OTPInput
                value={otpValue}
                onChange={handleOnChangeOtpValue}
                length={6}
              />
            </>
          )}
        </Box>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} sx={{ mr: 1 }}>
            {t('general.cancel')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
